/**
 * All authoritative numeric constants for Sai Clicker.
 *
 * Source of truth: `docs/09_progression_math.md`. When you change a number
 * here, update the corresponding table in the doc (and vice versa).
 *
 * All arrays indexed by epoch are 12-long and zero-indexed by `epoch - 1`.
 * Use the typed helpers in this file instead of raw indexing — they let the
 * type system catch out-of-range mistakes.
 */
import { ALL_EPOCHS, type Epoch, type PhraseEntry, type PhraseLayer } from './types.js';

// ---------------------------------------------------------------------------
// Per-epoch tap base value (`docs/09` §I.3)
// ---------------------------------------------------------------------------
const BASE_TAP_VALUES: readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] = [
  1, // epoch 1 — Spark
  3, // epoch 2
  10, // epoch 3
  50,
  250,
  1_500,
  8_000,
  50_000,
  300_000,
  2_000_000,
  15_000_000,
  100_000_000,
] as const;

// ---------------------------------------------------------------------------
// Per-epoch conversion base (`docs/09` §I.5)
// Spirit balls = floor( sqrt(awarenesses / conversionBase[epoch]) )
// ×3 per epoch. Early-game pacing is now driven by the lower EPOCH_THRESHOLDS
// for epochs 1-4 (see below) plus the PHRASE_LAYER_MULTIPLIERS that already
// boost gain on word phrases — so we keep conversion base on the conservative
// side that matches `docs/09` §V.2 target durations.
// ---------------------------------------------------------------------------
const CONVERSION_BASE_VALUES: readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] = [
  100, 300, 900, 2_700, 8_100, 24_300, 72_900, 218_700, 656_100, 1_968_300, 5_904_900, 17_714_700,
] as const;

// ---------------------------------------------------------------------------
// Epoch thresholds — spirit balls required to advance to NEXT epoch.
// `EPOCH_THRESHOLDS[epoch - 1]` = ball count needed to leave that epoch.
// `EPOCH_THRESHOLDS[11]` (epoch 12) is the prestige threshold.
// (`docs/09` §V.2)
//
// JB pacing review (2026-05-24): epochs 1-4 lowered for faster early game
// (epoch 1 ~10 min, 2 ~1h, 3 ~half-day, 4 ~1 day). Late thresholds 5-12
// unchanged — they already match the cozy "days→weeks→months" curve.
// ---------------------------------------------------------------------------
const EPOCH_THRESHOLD_VALUES: readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] = [
  5, // 1 → 2   (~10 min active play)
  20, // 2 → 3   (~1 hour)
  80, // 3 → 4   (~half a day in short sessions)
  400, // 4 → 5   (~a day)
  4_000, // 5 → 6   (~few days)
  24_000, // 6 → 7   (~a week)
  140_000, // 7 → 8   (~two weeks)
  1_000_000, // 8 → 9   (~3-4 weeks)
  7_000_000, // 9 → 10  (~6-8 weeks)
  50_000_000, // 10 → 11 (~2-3 months)
  350_000_000, // 11 → 12 (~3-4 months)
  2_500_000_000, // 12 → Enlightenment (~6-7 months total)
] as const;

// ---------------------------------------------------------------------------
// Floating phrase duration on screen (`docs/09` §I.3)
// ---------------------------------------------------------------------------
const PHRASE_DURATIONS_SEC: readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] = [1.5, 1.6, 1.7, 1.7, 1.8, 1.8, 1.9, 1.9, 2.0, 2.0, 2.2, 2.5] as const;

// ---------------------------------------------------------------------------
// Random jitter range applied to every tap (`docs/09` §I.2)
// Final value is multiplied by a uniform sample in [TAP_RANDOM_MIN, TAP_RANDOM_MAX].
// ---------------------------------------------------------------------------
export const TAP_RANDOM_MIN = 0.85;
export const TAP_RANDOM_MAX = 1.15;

// ---------------------------------------------------------------------------
// Special tap milestones — phrases that override the weighted pick
// (`docs/09` §VII.2 special on10thTap)
// ---------------------------------------------------------------------------
export const SPECIAL_TAP_INTERVALS = {
  on10thTap: 10,
  on100thTap: 100,
  on1000thTap: 1000,
} as const;

// ---------------------------------------------------------------------------
// Phrase-layer gain multipliers — the floating phrase isn't just decoration:
// its layer drives the actual awareness gain (`docs/09` §I.2).
//
//   numeric    ×1   — baseline ("+1" really means +1)
//   sensory    ×2   — "вижу", "слышу"
//   body       ×3   — "здесь", "есть"
//   elemental  ×5   — "свет", "корень"
//   awareness  ×10  — "я слышу", "путь рядом"
//   program    ×20  — (epochs 4+) "программа видна"
//   creator    ×50  — (epochs 8+) "я и есть искра"
//
// Word phrases don't print the number — but the counter ticks up by the
// real amount, creating per-tap variance and "wow" moments.
// ---------------------------------------------------------------------------
export const PHRASE_LAYER_MULTIPLIERS: Readonly<Record<PhraseLayer, number>> = {
  numeric: 1,
  sensory: 2,
  body: 3,
  elemental: 5,
  awareness: 10,
  program: 20,
  creator: 50,
};

// ---------------------------------------------------------------------------
// Special milestone multipliers — stacked on top of the layer bonus.
// 10th tap ×5, 100th tap ×25, 1000th tap ×100. These are intentionally
// generous to give the player a real "wow" at every milestone.
// ---------------------------------------------------------------------------
export const SPECIAL_TAP_MULTIPLIERS: Readonly<
  Record<NonNullable<PhraseEntry['special']>, number>
> = {
  on10thTap: 5,
  on100thTap: 25,
  on1000thTap: 100,
};

// ---------------------------------------------------------------------------
// Tap cooldown & rhythm — JB 2026-05-24 reframed tapping from speed-based
// to cadence-based.
//
// Default cooldown: every accepted tap blocks the Spark for `cooldownMs`.
// Taps that arrive sooner are silently rejected (no awareness, no UI
// reaction beyond a brief ring jitter). Upgrades reduce `cooldownMs` down
// toward MIN_TAP_COOLDOWN_MS.
//
// Rhythm streak: if the next tap lands within RHYTHM_WINDOW_MS of when
// the cooldown completed, the player is "in rhythm" and the streak
// increments. Late taps reset the streak to 1 but still count.
//
// Old RHYTHM_MAX_GAP_MS (3_000) is preserved for backwards compat with
// any callers / docs that still reference it; it's no longer used by the
// reducer.
// ---------------------------------------------------------------------------
export const BASE_TAP_COOLDOWN_MS = 1_500;
export const MIN_TAP_COOLDOWN_MS = 200;
export const RHYTHM_WINDOW_MS = 400;
export const RHYTHM_MAX_GAP_MS = 3_000;

/**
 * Streak multiplier — how much the rhythm streak boosts a tap's value.
 * Linear ramp: streak 1 → ×1.0, streak 11 → ×1.5, streak 21 → ×2.0.
 * Cozy game — we don't want runaway exponentials.
 */
export function streakMultiplier(streak: number): number {
  return 1 + Math.max(0, streak - 1) * 0.05;
}

// ---------------------------------------------------------------------------
// Typed accessors — these are the only sanctioned way to read epoch-keyed data.
// They give the type checker enough information to know the result is `number`,
// not `number | undefined` (under `noUncheckedIndexedAccess`).
// ---------------------------------------------------------------------------

/** Throws if epoch is out of [1..12]. */
function assertEpoch(epoch: Epoch): asserts epoch is Epoch {
  if (!ALL_EPOCHS.includes(epoch)) {
    throw new RangeError(`Invalid epoch: ${String(epoch)}. Must be 1..12.`);
  }
}

export function getBaseTap(epoch: Epoch): number {
  assertEpoch(epoch);
  // safe: BASE_TAP_VALUES is a fixed 12-tuple, epoch is 1..12
  return BASE_TAP_VALUES[epoch - 1]!;
}

export function getConversionBase(epoch: Epoch): number {
  assertEpoch(epoch);
  return CONVERSION_BASE_VALUES[epoch - 1]!;
}

export function getEpochThreshold(epoch: Epoch): number {
  assertEpoch(epoch);
  return EPOCH_THRESHOLD_VALUES[epoch - 1]!;
}

export function getPhraseDurationSec(epoch: Epoch): number {
  assertEpoch(epoch);
  return PHRASE_DURATIONS_SEC[epoch - 1]!;
}

/** Next epoch, or null if already at 12 (and prestige is the only path). */
export function getNextEpoch(epoch: Epoch): Epoch | null {
  if (epoch === 12) {
    return null;
  }
  return (epoch + 1) as Epoch;
}
